
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import LoadingScreen from "@/components/LoadingScreen";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Mail, Trash2, Download } from "lucide-react";
import { UserWithProfile } from "@/types/admin";
import { useAdminData } from "@/hooks/useAdminData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface NewsletterSubscription {
  id: string;
  email: string;
  subscribed_at: string;
  is_active: boolean;
  user_id: string | null;
}

const AdminUsers = () => {
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const { 
    users, 
    newsletterSubscriptions,
    loading,
  } = useAdminData(isAdmin);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);

  const handleToggleSubscription = async (userId: string, userEmail: string, currentlySubscribed: boolean) => {
    setTogglingUserId(userId);
    try {
      if (currentlySubscribed) {
        // Unsubscribe: update existing subscription to inactive
        const { error } = await supabase
          .from('newsletter_subscriptions')
          .update({ is_active: false })
          .eq('email', userEmail.toLowerCase());
        
        if (error) throw error;
        toast.success(`${userEmail} has been unsubscribed`);
      } else {
        // Subscribe: upsert a subscription record
        const { error } = await supabase
          .from('newsletter_subscriptions')
          .upsert({ 
            email: userEmail.toLowerCase(), 
            is_active: true,
            user_id: userId 
          }, { onConflict: 'email' });
        
        if (error) throw error;
        toast.success(`${userEmail} has been subscribed`);
      }
    } catch (error: any) {
      console.error("Error toggling subscription:", error);
      toast.error(`Failed to update subscription: ${error?.message || 'Unknown error'}`);
    } finally {
      setTogglingUserId(null);
    }
  };

  // Show loading while checking role
  if (roleLoading) {
    return <LoadingScreen />;
  }

  // Redirect non-admins to homepage
  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  const formatUserName = (profile: UserWithProfile["profile"]) => {
    if (!profile) return "No Profile";
    
    const firstName = profile.first_name || "";
    const lastName = profile.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    
    return fullName || "Unknown User";
  };

  const calculateAge = (dateOfBirth: string | null | undefined): number | null => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Create a map of user emails to their subscription status
  const subscriptionMap = new Map();
  newsletterSubscriptions.forEach(sub => {
    subscriptionMap.set(sub.email.toLowerCase(), sub);
  });

  // Get standalone newsletter subscriptions (not linked to any registered user)
  const standaloneSubscriptions = newsletterSubscriptions.filter(sub => 
    sub.is_active && !users.some(user => user.email.toLowerCase() === sub.email.toLowerCase())
  );

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    setDeletingUserId(userId);
    try {
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: {
          targetUserId: userId,
          userEmail: userEmail
        }
      });
      
      if (error) {
        console.error("Error deleting user:", error);
        toast.error(`Failed to delete user: ${error.message}`);
        return;
      }

      if (data?.error) {
        console.error("Server error deleting user:", data.error);
        toast.error(`Failed to delete user: ${data.error}`);
        return;
      }

      toast.success(`User ${userEmail} has been deleted successfully`);
      // The useAdminData hook should automatically refresh the data
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast.error(`Failed to delete user: ${error?.message || 'Unknown error'}`);
    } finally {
      setDeletingUserId(null);
    }
  };

  const exportNewsletterSubscriptions = () => {
    const activeSubscriptions = newsletterSubscriptions.filter(sub => sub.is_active);
    const emailList = activeSubscriptions.map(sub => sub.email).join('\n');
    
    const blob = new Blob([emailList], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscriptions-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Newsletter subscriptions exported successfully');
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 space-y-8">
        <div className="mb-8">
          <h1 className="font-poppins font-bold text-4xl mb-4 text-foreground">All Users & Subscribers</h1>
          <p className="font-inter text-xl text-muted-foreground">
            Complete management of all registered users and newsletter subscriptions
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Registered Users Section */}
            <div>
              <h2 className="font-poppins text-2xl font-semibold mb-4 text-foreground">
                Registered Users ({users.length})
              </h2>
              {users.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground font-inter">
                  No registered users found.
                </p>
              ) : (
                <div className="bg-card rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-muted/50">
                        <TableHead className="font-poppins text-foreground">Name</TableHead>
                        <TableHead className="font-poppins text-foreground">Email</TableHead>
                        <TableHead className="font-poppins text-foreground">Age</TableHead>
                        <TableHead className="font-poppins text-foreground">Phone</TableHead>
                        <TableHead className="font-poppins text-foreground">School</TableHead>
                        <TableHead className="font-poppins text-foreground">Joined</TableHead>
                        <TableHead className="font-poppins text-foreground">Last Sign In</TableHead>
                        <TableHead className="font-poppins text-foreground">Newsletter</TableHead>
                        <TableHead className="font-poppins text-foreground">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => {
                        const subscription = subscriptionMap.get(user.email.toLowerCase());
                        const age = calculateAge(user.profile?.date_of_birth);
                        return (
                          <TableRow key={user.id} className="border-border hover:bg-muted/50">
                            <TableCell className="font-medium font-inter text-foreground">
                              {formatUserName(user.profile)}
                            </TableCell>
                            <TableCell className="font-inter text-foreground">
                              {user.email}
                            </TableCell>
                            <TableCell className="font-inter text-sm text-foreground">
                              {age !== null ? age : <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell className="font-inter text-sm text-foreground">
                              {user.profile?.phone_number || <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell className="font-inter text-sm text-foreground">
                              {user.profile?.school || <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell className="font-inter text-foreground">
                              {new Date(user.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="font-inter text-foreground">
                              {user.last_sign_in_at 
                                ? new Date(user.last_sign_in_at).toLocaleDateString()
                                : <span className="text-muted-foreground">Never</span>
                              }
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const isSubscribed = subscription?.is_active !== false;
                                return (
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={isSubscribed}
                                      onCheckedChange={() => handleToggleSubscription(user.id, user.email, isSubscribed)}
                                      disabled={togglingUserId === user.id}
                                    />
                                    <span className={`text-sm font-medium ${isSubscribed ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                                      {isSubscribed ? 'Subscribed' : 'Unsubscribed'}
                                    </span>
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    disabled={deletingUserId === user.id}
                                  >
                                    {deletingUserId === user.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="w-4 h-4" />
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete User</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete user "{user.email}"? This action cannot be undone and will permanently remove all their data.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteUser(user.id, user.email)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete User
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            {/* Newsletter-Only Subscriptions Section */}
            {standaloneSubscriptions.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-poppins text-2xl font-semibold text-foreground">
                    Newsletter-Only Subscriptions ({standaloneSubscriptions.length})
                  </h2>
                  <Button onClick={exportNewsletterSubscriptions} variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Export All
                  </Button>
                </div>
                <div className="bg-card rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-muted/50">
                        <TableHead className="font-poppins text-foreground">Email</TableHead>
                        <TableHead className="font-poppins text-foreground">Subscribed Date</TableHead>
                        <TableHead className="font-poppins text-foreground">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {standaloneSubscriptions.map((subscription) => (
                        <TableRow key={subscription.id} className="border-border hover:bg-muted/50">
                          <TableCell className="font-medium font-inter text-foreground">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-primary" />
                              {subscription.email}
                            </div>
                          </TableCell>
                          <TableCell className="font-inter text-foreground">
                            {new Date(subscription.subscribed_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <span className="px-2 py-1 rounded-full text-xs bg-primary/10 text-primary">
                              Newsletter Only
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
